import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DocumentItemEntity,
  DocumentPublicationTypeAllItems,
  DocumentSectionTypeAllItems
} from '../../../../../meta-data/app-meta-data.service';
import {
  StructuredDocumentFavoriteModel,
  StructuredDocumentHierarchyGroupModel,
  StructuredDocumentHierarchyPartModel,
  StructuredDocumentHierarchySectionModel,
  StructuredDocumentNavItemModel,
  StructuredDocumentSectionReferenceModel,
  StructuredDocumentSectionModel,
  StructuredDocumentViewerModel
} from '../../models/structured-document-viewer.model';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';
import { StructuredDocumentViewerService } from '../../services/structured-document-viewer.service';

interface StructuredDocumentPageOptionModel {
  documentSectionId: number;
  label: string;
}

interface StructuredDocumentTocPartMetaModel {
  rangeText: string;
  countText: string;
}

interface StructuredDocumentTocPartModel {
  part: StructuredDocumentSectionModel;
  rangeText: string;
  countText: string;
  children: StructuredDocumentSectionModel[];
}

interface StructuredDocumentTocGroupModel {
  key: number | string;
  eyebrow: string;
  title: string;
  parts: StructuredDocumentTocPartModel[];
}

interface StructuredDocumentResolvedReferenceModel extends StructuredDocumentSectionReferenceModel {
  section: StructuredDocumentSectionModel;
}

interface StructuredDocumentReferenceDisplayModel extends StructuredDocumentResolvedReferenceModel {
  displayTitle: string;
}

interface StructuredDocumentHierarchySearchRequestModel {
  searchText: string;
  version: number;
}

interface StructuredDocumentFavoriteGroupModel {
  key: string;
  title: string;
  items: StructuredDocumentFavoriteModel[];
}

type StructuredViewerMode = 'generic' | 'reference';
type ReferencePublicationKind = 'far' | 'aim';
type ReferenceView = 'toc' | 'part' | 'reading';

@Component({
  selector: 'app-structured-document-viewer',
  templateUrl: './structured-document-viewer.component.html',
  styleUrls: ['./structured-document-viewer.component.scss']
})
export class StructuredDocumentViewerComponent implements OnChanges, OnDestroy {
  @ViewChild('readingSearchInput') private readingSearchInput?: ElementRef<HTMLInputElement>;

  @Input() documentItem: DocumentItemEntity;
  @Input() viewerData: StructuredDocumentViewerModel;
  @Input() hideHeader: boolean = false;
  @Input() sectionReferences: StructuredDocumentSectionReferenceModel[] = [];
  // Sync the toc/part navigation levels with ?pub=&part= query params so part
  // contents pages are addressable. Only the FAR/AIM page opts in; the lesson
  // companion embeds this component without touching the URL.
  @Input() enableRouteSync: boolean = false;

  viewerMode: StructuredViewerMode = 'generic';
  referenceKind: ReferencePublicationKind = 'far';
  activeReferenceTab: ReferencePublicationKind = 'far';
  referenceView: ReferenceView = 'toc';
  searchText: string = '';
  activeReferenceDocumentItemId?: number;
  isReferenceSwitchLoading = false;
  referenceSwitchError = '';

  private readonly aimReferenceDocumentItemId = 1043;
  private readonly viewerDataCache = new Map<ReferencePublicationKind, StructuredDocumentViewerModel>();
  private readonly documentItemIdByKind = new Map<ReferencePublicationKind, number>();

  contentsPart: StructuredDocumentSectionModel | null = null;
  contentsSections: StructuredDocumentSectionModel[] = [];
  contentsPartEyebrow: string = '';
  contentsPartMeta: StructuredDocumentTocPartMetaModel | null = null;
  navTree: StructuredDocumentNavItemModel[] = [];
  tocGroups: StructuredDocumentTocGroupModel[] = [];
  displayTocGroups: StructuredDocumentTocGroupModel[] = [];
  leafSections: StructuredDocumentSectionModel[] = [];
  activeSection: StructuredDocumentSectionModel | null = null;
  activePart: StructuredDocumentSectionModel | null = null;
  activeIndex: number = 0;
  isSectionLoading: boolean = false;
  sectionLoadError: string = '';
  isPageMenuOpen: boolean = false;
  isReferenceOverflowOpen: boolean = false;
  isNavOpen: boolean = false;
  selectedLeafSectionId: number | null = null;
  pageOptions: StructuredDocumentPageOptionModel[] = [];
  referencedSections: StructuredDocumentReferenceDisplayModel[] = [];
  primaryReferencedSection: StructuredDocumentReferenceDisplayModel | null = null;
  overflowReferencedSections: StructuredDocumentReferenceDisplayModel[] = [];
  readonly hierarchySearchSkeletonRows: number[] = [1, 2, 3, 4, 5];
  isHierarchySearchLoading: boolean = false;
  isHierarchySearchPending: boolean = false;
  hierarchySearchError: string = '';
  showFavorites: boolean = false;
  favorites: StructuredDocumentFavoriteModel[] = [];
  displayFavoriteGroups: StructuredDocumentFavoriteGroupModel[] = [];
  favoritesLoading: boolean = false;
  favoritesError: string = '';
  favoriteActionError: string = '';
  isHeaderSearchExpanded: boolean = false;

  private sectionLookup = new Map<number, StructuredDocumentSectionModel>();
  private childLookup = new Map<number, StructuredDocumentSectionModel[]>();
  private loadedSectionIds = new Set<number>();
  private loadingSectionIds = new Set<number>();
  private readonly hierarchySearchText$ = new Subject<StructuredDocumentHierarchySearchRequestModel>();
  private searchTocGroups: StructuredDocumentTocGroupModel[] = [];
  private readonly tocMetaByPartId = new Map<number, StructuredDocumentTocPartMetaModel>();
  private readonly subscriptions = new Subscription();
  private hierarchySearchVersion = 0;
  private routePub: ReferencePublicationKind | null = null;
  private routePartId: number | null = null;
  private routeSectionReferenceKey: string | null = null;
  private isApplyingRoutePub = false;
  private favoritesLoaded = false;
  private pendingFavorite: StructuredDocumentFavoriteModel | null = null;

  constructor(
    private structuredDocumentViewerService: StructuredDocumentViewerService,
    private hostElementRef: ElementRef<HTMLElement>,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe(params => {
        this.routePub = params['pub'] === 'aim' ? 'aim' : (params['pub'] === 'far' ? 'far' : null);
        const routePartId = parseInt(params['part'], 10);
        this.routePartId = isNaN(routePartId) ? null : routePartId;
        this.routeSectionReferenceKey = params['section']
          ? `${params['section']}`.trim().toLowerCase()
          : null;
        this.showFavorites = params['favorites'] === 'true';
        if (this.showFavorites) {
          this.loadFavorites();
        }
        this.applyRouteState();
      })
    );
    this.subscriptions.add(
      this.hierarchySearchText$
        .pipe(
          debounceTime(250),
          distinctUntilChanged((previous, current) => previous.searchText === current.searchText && previous.version === current.version),
          switchMap(request => this.searchReferenceHierarchy(request.searchText, request.version))
        )
        .subscribe()
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.viewerData && !changes.sectionReferences) {
      return;
    }

    if (changes.viewerData) {
      this.searchText = '';
      this.loadedSectionIds.clear();
      this.loadingSectionIds.clear();
      this.isSectionLoading = false;
      this.sectionLoadError = '';
      this.isNavOpen = false;
      this.isReferenceSwitchLoading = false;
      this.referenceSwitchError = '';
      this.resetHierarchySearchState();
      this.viewerDataCache.clear();
      this.documentItemIdByKind.clear();
      this.rebuildViewModel();
      this.seedPrimaryPublicationCache();
      this.loadFavorites();
      this.applyRouteState();
      return;
    }

    this.syncReferencedSections();
    this.navigateToFirstSectionReference();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get currentSection(): StructuredDocumentSectionModel | null {
    return this.activeSection;
  }

  get currentSectionLabel(): string {
    return this.currentSection?.Label || this.currentSection?.SectionKey || '';
  }

  get headerRegNumber(): string {
    return this.parseSectionLabel(this.currentSectionLabel).regNumber;
  }

  get headerRegEyebrow(): string {
    return this.parseSectionLabel(this.currentSectionLabel).eyebrow;
  }

  get currentSectionTitle(): string {
    return this.currentSection?.Title || this.viewerData?.PublicationName || this.documentItem?.DocumentItemName || '';
  }

  get isReferenceLayout(): boolean {
    return this.viewerMode === 'reference';
  }

  get referencePublicationTitle(): string {
    if (this.activeReferenceTab === 'aim') {
      return 'Aeronautical Information Manual';
    }

    return this.viewerData?.PublicationName || 'Federal Aviation Regulations';
  }

  get referenceMetaText(): string {
    if (this.activeReferenceTab === 'aim') {
      return this.viewerData?.VersionLabel || this.viewerData?.SourceVersion || 'FAA AIM';
    }

    return 'Title 14 CFR';
  }

  get searchPlaceholder(): string {
    return this.activeReferenceTab === 'aim'
      ? 'Search sections and paragraphs'
      : 'Search parts and sections';
  }

  get showTocSkeleton(): boolean {
    return this.isReferenceSwitchLoading || this.isHierarchySearchActive;
  }

  get isReadingSearchOpen(): boolean {
    return this.isReferenceLayout
      && this.referenceView === 'reading'
      && !!(this.searchText || '').trim();
  }

  get shouldUseHierarchySearchResults(): boolean {
    return this.isReferenceLayout
      && !!(this.searchText || '').trim();
  }

  get isHierarchySearchActive(): boolean {
    return this.isHierarchySearchPending || this.isHierarchySearchLoading;
  }

  get currentSubchapterTitle(): string {
    if (this.referenceKind === 'aim') {
      if (this.activePart?.DocumentSectionTypeId === DocumentSectionTypeAllItems.Appendix) {
        return 'Appendix';
      }

      const chapter = this.activePart?.ParentDocumentSectionId
        ? this.sectionLookup.get(this.activePart.ParentDocumentSectionId)
        : null;

      if (!chapter) {
        return '';
      }

      return chapter.Label && chapter.Title
        ? `${chapter.Label} — ${chapter.Title}`
        : (chapter.Title || chapter.Label || '');
    }

    const subchapter = this.activePart?.ParentDocumentSectionId
      ? this.sectionLookup.get(this.activePart.ParentDocumentSectionId)
      : null;

    return subchapter?.Title || '';
  }

  selectSection(section: StructuredDocumentSectionModel): void {
    this.closeNav();

    const targetSection = this.resolveGenericTargetSection(section);
    this.activeSection = targetSection;
    this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === targetSection.DocumentSectionId));
    this.selectedLeafSectionId = targetSection.DocumentSectionId || null;
    this.sectionLoadError = '';
    this.ensureSectionContentLoaded(targetSection);
  }

  openTocPart(part: StructuredDocumentSectionModel): void {
    if (!part || !part.DocumentSectionId || this.isReferenceSwitchLoading) {
      return;
    }

    if (this.enableRouteSync) {
      this.navigateToRoute(this.activeReferenceTab, part.DocumentSectionId);
    }

    this.showPartContents(part);
  }

  openPartSection(section: StructuredDocumentSectionModel): void {
    if (!this.contentsPart || !section?.DocumentSectionId) {
      return;
    }

    this.activePart = this.contentsPart;
    this.activeSection = section;
    this.sectionLoadError = '';
    this.applyActivePartLeaves();
    this.syncReferenceDisplaySections();
    this.isPageMenuOpen = false;
    this.clearReferenceSearch();
    this.referenceView = 'reading';
  }

  clearReferenceSearch(): void {
    this.searchText = '';
    this.isHeaderSearchExpanded = false;
    this.resetHierarchySearchState();
  }

  toggleHeaderSearch(): void {
    if (this.isHeaderSearchExpanded) {
      this.clearReferenceSearch();
      return;
    }

    this.isHeaderSearchExpanded = true;
    setTimeout(() => this.readingSearchInput?.nativeElement.focus());
  }

  backToContents(): void {
    this.isPageMenuOpen = false;

    // Reading steps back to the part contents page; the part contents page
    // steps back to the main table of contents.
    if (this.referenceView === 'reading' && this.activePart?.DocumentSectionId) {
      if (this.enableRouteSync) {
        this.navigateToRoute(this.activeReferenceTab, this.activePart.DocumentSectionId);
      }

      this.showPartContents(this.activePart);
      return;
    }

    if (this.enableRouteSync) {
      this.navigateToRoute(this.activeReferenceTab, null);
    }

    this.referenceView = 'toc';
  }

  selectReferenceTab(tab: ReferencePublicationKind): void {
    if (this.activeReferenceTab === tab && !this.isReferenceSwitchLoading) {
      return;
    }

    if (this.enableRouteSync && !this.isApplyingRoutePub) {
      // A user tab switch resets navigation to that publication's main TOC. A
      // route-driven switch must keep the URL (it may carry a deep-linked part).
      this.navigateToRoute(tab, null);
    }

    this.searchText = '';
    this.referenceSwitchError = '';
    this.isPageMenuOpen = false;
    this.resetHierarchySearchState();
    this.referenceView = 'toc';

    const cachedViewer = this.viewerDataCache.get(tab);
    if (cachedViewer) {
      this.applyPublication(tab, cachedViewer);
      return;
    }

    const documentItemId = this.documentItemIdByKind.get(tab);
    if (!documentItemId) {
      this.referenceSwitchError = tab === 'aim'
        ? 'AIM reference document is not configured.'
        : 'FAR reference document is not configured.';
      return;
    }

    this.activeReferenceTab = tab;
    this.isReferenceSwitchLoading = true;

    this.structuredDocumentViewerService.getStructuredDocumentViewer(documentItemId)
      .pipe(finalize(() => {
        this.isReferenceSwitchLoading = false;
      }))
      .subscribe({
        next: viewer => {
          if (!viewer?.DocumentVersionId || !viewer?.Sections?.length) {
            this.referenceSwitchError = tab === 'aim'
              ? 'AIM reference content is not available yet.'
              : 'FAR reference content is not available yet.';
            return;
          }

          this.viewerDataCache.set(tab, viewer);
          this.applyPublication(tab, viewer);
        },
        error: () => {
          this.referenceSwitchError = tab === 'aim'
            ? 'Unable to load AIM reference content right now.'
            : 'Unable to load FAR reference content right now.';
        }
      });
  }

  selectLeafSection(section: StructuredDocumentSectionModel): void {
    this.isPageMenuOpen = false;
    this.activeSection = section;
    this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === section.DocumentSectionId));
    this.selectedLeafSectionId = section.DocumentSectionId || null;
    this.sectionLoadError = '';
    this.syncReferenceDisplaySections();
    this.ensureSectionContentLoaded(section);
  }

  selectSectionReference(reference: StructuredDocumentResolvedReferenceModel): void {
    if (!reference?.section) {
      return;
    }

    this.closeReferenceOverflow();
    this.navigateToReferencedSection(reference.section);
  }

  isSectionReferenceActive(reference: StructuredDocumentResolvedReferenceModel): boolean {
    return this.activeSection?.DocumentSectionId === reference?.section?.DocumentSectionId;
  }

  onPageDropdownChange(documentSectionIdValue: number | string): void {
    const documentSectionId = typeof documentSectionIdValue === 'number'
      ? documentSectionIdValue
      : parseInt(documentSectionIdValue, 10);
    if (isNaN(documentSectionId)) {
      return;
    }

    const section = this.leafSections.find(item => item.DocumentSectionId === documentSectionId);
    if (section) {
      this.selectLeafSection(section);
    }
  }

  previousSection(): void {
    if (this.activeIndex > 0) {
      this.selectLeafSection(this.leafSections[this.activeIndex - 1]);
    }
  }

  nextSection(): void {
    if (this.activeIndex < this.leafSections.length - 1) {
      this.selectLeafSection(this.leafSections[this.activeIndex + 1]);
    }
  }

  get canOpenPageMenu(): boolean {
    return !this.isReferenceSwitchLoading && !this.referenceSwitchError && this.leafSections.length > 1;
  }

  togglePageMenu(): void {
    if (!this.canOpenPageMenu) {
      return;
    }

    this.isPageMenuOpen = !this.isPageMenuOpen;
    if (this.isPageMenuOpen) {
      this.scrollActivePageMenuItemIntoView();
    }
  }

  closePageMenu(): void {
    this.isPageMenuOpen = false;
  }

  toggleReferenceOverflow(event: MouseEvent): void {
    event.stopPropagation();
    this.isReferenceOverflowOpen = !this.isReferenceOverflowOpen;
  }

  closeReferenceOverflow(): void {
    this.isReferenceOverflowOpen = false;
  }

  toggleNav(): void {
    this.isNavOpen = !this.isNavOpen;
  }

  closeNav(): void {
    this.isNavOpen = false;
  }

  selectPage(section: StructuredDocumentSectionModel): void {
    this.closePageMenu();
    this.selectLeafSection(section);
  }

  onSearchTextChange(searchText: string): void {
    this.searchText = searchText || '';

    if (this.showFavorites) {
      this.rebuildFavoriteGroups();
      return;
    }

    if (!this.isReferenceLayout) {
      return;
    }

    const trimmedSearchText = this.searchText.trim();
    if (!trimmedSearchText) {
      this.resetHierarchySearchState();
      return;
    }

    this.isHierarchySearchPending = true;
    this.hierarchySearchVersion++;
    this.hierarchySearchText$.next({
      searchText: trimmedSearchText,
      version: this.hierarchySearchVersion
    });
  }

  pageMenuItemLabel(section: StructuredDocumentSectionModel): string {
    return section?.Label || section?.SectionKey || '';
  }

  openTocSearchResult(part: StructuredDocumentSectionModel, section: StructuredDocumentSectionModel): void {
    if (!part?.DocumentSectionId || !section?.DocumentSectionId) {
      return;
    }

    const resolvedPart = this.sectionLookup.get(part.DocumentSectionId) || part;
    if (this.activePart?.DocumentSectionId !== resolvedPart.DocumentSectionId) {
      this.activePart = resolvedPart;
      this.applyActivePartLeaves();
    }

    const resolvedSection = this.leafSections.find(item => item.DocumentSectionId === section.DocumentSectionId)
      || this.sectionLookup.get(section.DocumentSectionId)
      || section;

    this.selectLeafSection(resolvedSection);
    this.clearReferenceSearch();
    this.referenceView = 'reading';
    this.searchText = '';
    this.resetHierarchySearchState();
    this.closeNav();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;

    if (this.isPageMenuOpen && !this.hostElementRef.nativeElement.contains(target)) {
      this.closePageMenu();
    }

    if (this.isReadingSearchOpen || this.isHeaderSearchExpanded) {
      const searchWrap = this.hostElementRef.nativeElement.querySelector('.sdv-header__search, .sdv-toc__search-wrap');
      if (searchWrap && !searchWrap.contains(target)) {
        this.clearReferenceSearch();
      }
    }

    if (this.isReferenceOverflowOpen) {
      const referenceSelector = this.hostElementRef.nativeElement.querySelector('.sdv-reference-selector');
      if (referenceSelector && !referenceSelector.contains(target)) {
        this.closeReferenceOverflow();
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closePageMenu();
    this.closeNav();
    this.closeReferenceOverflow();

    if (this.isReadingSearchOpen || this.isHeaderSearchExpanded) {
      this.clearReferenceSearch();
    }
  }

  isSectionActive(section: StructuredDocumentSectionModel): boolean {
    return this.activeSection?.DocumentSectionId === section.DocumentSectionId;
  }

  trackByNav(index: number, item: StructuredDocumentNavItemModel): number | string {
    return item.section.DocumentSectionId || item.section.SectionKey || index;
  }

  trackByTocGroup(index: number, group: StructuredDocumentTocGroupModel): number | string {
    return group.key || index;
  }

  trackByTocPart(index: number, item: StructuredDocumentTocPartModel): number | string {
    return item.part.DocumentSectionId || item.part.SectionKey || index;
  }

  trackBySection(index: number, section: StructuredDocumentSectionModel): number | string {
    return section.DocumentSectionId || section.SectionKey || index;
  }

  trackBySectionReference(index: number, reference: StructuredDocumentResolvedReferenceModel): number | string {
    return reference.documentSectionId || index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByFavoriteGroup(index: number, group: StructuredDocumentFavoriteGroupModel): string {
    return group.key;
  }

  trackByFavorite(index: number, favorite: StructuredDocumentFavoriteModel): number | string {
    return favorite.UserDocumentSectionFavoriteId
      || `${favorite.DocumentItemId || 0}|${favorite.SectionReferenceKey}`;
  }

  toggleFavorites(): void {
    this.showFavorites = !this.showFavorites;
    this.searchText = '';
    this.favoritesError = '';
    this.favoriteActionError = '';
    this.resetHierarchySearchState();

    if (this.showFavorites) {
      this.referenceView = 'toc';
      this.loadFavorites();
    }

    if (this.enableRouteSync) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          favorites: this.showFavorites ? 'true' : null,
          part: this.showFavorites ? null : this.routePartId,
          section: null
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  browseFavoritesPublication(kind: ReferencePublicationKind): void {
    this.showFavorites = false;
    this.searchText = '';

    if (this.enableRouteSync) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { favorites: null, pub: kind, part: null },
        queryParamsHandling: 'merge'
      });
    }

    if (this.activeReferenceTab !== kind) {
      this.selectReferenceTab(kind);
    }
  }

  toggleSectionFavorite(section: StructuredDocumentSectionModel, event?: MouseEvent): void {
    event?.stopPropagation();

    const documentItemId = this.activeReferenceDocumentItemId ?? this.documentItem?.DocumentItemId;
    const documentSectionId = section?.DocumentSectionId;
    const sectionReferenceKey = (section?.SectionKey || '').trim().toLowerCase();

    if (!documentItemId || !documentSectionId || !sectionReferenceKey || section.FavoriteSaving) {
      return;
    }

    const wasFavorite = section.IsFavorite === true;
    const favoriteId = wasFavorite
      ? this.favorites.find(favorite =>
        favorite.DocumentItemId === documentItemId
        && (favorite.SectionReferenceKey || '').trim().toLowerCase() === sectionReferenceKey
      )?.UserDocumentSectionFavoriteId
      : undefined;

    if (wasFavorite && !favoriteId) {
      this.favoriteActionError = 'Unable to find this favorite. Refresh the page and try again.';
      return;
    }

    this.favoriteActionError = '';
    section.IsFavorite = !wasFavorite;
    section.FavoriteSaving = true;
    this.syncSectionFavoriteAcrossCache(documentItemId, sectionReferenceKey, !wasFavorite, true);

    const request = wasFavorite
      ? this.structuredDocumentViewerService.removeStructuredDocumentFavorite(favoriteId as number)
      : this.structuredDocumentViewerService.addStructuredDocumentFavorite(
        documentItemId,
        documentSectionId,
        this.viewerData?.DocumentVersionId
      );

    request.pipe(finalize(() => {
      this.syncSectionFavoriteAcrossCache(documentItemId, sectionReferenceKey, !wasFavorite, false);
    })).subscribe({
      next: () => this.loadFavorites(true),
      error: () => {
        this.syncSectionFavoriteAcrossCache(documentItemId, sectionReferenceKey, wasFavorite, false);
        this.favoriteActionError = wasFavorite
          ? 'Unable to remove this favorite right now.'
          : 'Unable to add this favorite right now.';
      }
    });
  }

  removeFavorite(favorite: StructuredDocumentFavoriteModel, event: MouseEvent): void {
    event.stopPropagation();

    if (!favorite.DocumentItemId || !favorite.UserDocumentSectionFavoriteId || favorite.IsSaving) {
      return;
    }

    favorite.IsSaving = true;
    this.structuredDocumentViewerService
      .removeStructuredDocumentFavorite(favorite.UserDocumentSectionFavoriteId)
      .pipe(finalize(() => {
        favorite.IsSaving = false;
      }))
      .subscribe({
        next: () => {
          this.favorites = this.favorites.filter(item =>
            item.UserDocumentSectionFavoriteId !== favorite.UserDocumentSectionFavoriteId);
          this.syncSectionFavoriteAcrossCache(
            favorite.DocumentItemId || 0,
            favorite.SectionReferenceKey,
            false,
            false
          );
          this.rebuildFavoriteGroups();
        },
        error: () => {
          this.favoritesError = 'Unable to remove this favorite right now.';
        }
      });
  }

  openFavorite(favorite: StructuredDocumentFavoriteModel): void {
    if (!favorite.IsAvailable || !favorite.DocumentItemId || !favorite.CurrentDocumentSectionId) {
      return;
    }

    this.pendingFavorite = favorite;
    this.showFavorites = false;
    this.favoritesError = '';
    this.favoriteActionError = '';
    const kind: ReferencePublicationKind =
      favorite.DocumentPublicationTypeId === DocumentPublicationTypeAllItems.AIM ? 'aim' : 'far';

    if (this.enableRouteSync) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          favorites: null,
          pub: kind,
          part: null,
          section: favorite.SectionReferenceKey
        },
        queryParamsHandling: 'merge'
      });
      this.pendingFavorite = null;
      return;
    }

    const cachedViewer = this.viewerDataCache.get(kind);
    if (cachedViewer) {
      this.applyPublication(kind, cachedViewer);
      return;
    }

    this.selectReferenceTab(kind);
  }

  getSectionBlocks(section: StructuredDocumentSectionModel | null | undefined): any[] {
    if (!section || !section.Content || !section.Content.length) {
      return [];
    }

    const blocks = section.Content[0]?.BlockJson || [];

    return this.isReferenceLayout
      ? this.stripDuplicateSectionHeading(blocks, section)
      : blocks;
  }

  private stripDuplicateSectionHeading(blocks: any[], section: StructuredDocumentSectionModel): any[] {
    if (!blocks.length || blocks[0]?.t !== 'h') {
      return blocks;
    }

    const headingText = this.normalizeHeadingText(this.inlineNodesToText(blocks[0].c));
    const sectionTitle = this.normalizeHeadingText(section.Title);
    const sectionHeading = this.normalizeHeadingText(`${section.Label || section.SectionKey || ''} ${section.Title || ''}`);

    if (headingText === sectionTitle || headingText === sectionHeading || (sectionTitle && headingText.endsWith(` ${sectionTitle}`))) {
      return blocks.slice(1);
    }

    return blocks;
  }

  private inlineNodesToText(nodes: any[]): string {
    return (Array.isArray(nodes) ? nodes : [])
      .map(node => this.inlineNodeToText(node))
      .join('');
  }

  private inlineNodeToText(node: any): string {
    if (!node) {
      return '';
    }

    if (typeof node === 'string') {
      return node;
    }

    if (node.t === 'text') {
      return node.s || '';
    }

    return this.inlineNodesToText(node.c);
  }

  private normalizeHeadingText(value: string | null | undefined): string {
    return (value || '')
      .replace(/^§\s*/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private rebuildViewModel(): void {
    const sortedSections = (this.viewerData?.Sections || [])
      .slice()
      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

    this.sectionLookup = new Map<number, StructuredDocumentSectionModel>();
    this.childLookup = new Map<number, StructuredDocumentSectionModel[]>();
    this.referenceView = 'toc';
    this.contentsPart = null;
    this.contentsSections = [];
    this.contentsPartEyebrow = '';
    this.contentsPartMeta = null;
    this.isPageMenuOpen = false;
    this.navTree = [];
    this.tocGroups = [];
    this.displayTocGroups = [];
    this.searchTocGroups = [];
    this.tocMetaByPartId.clear();
    this.leafSections = [];
    this.activeSection = null;
    this.activePart = null;
    this.activeIndex = 0;
    this.selectedLeafSectionId = null;
    this.pageOptions = [];
    this.referencedSections = [];

    sortedSections.forEach(section => {
      if (!section.DocumentSectionId) {
        return;
      }

      this.sectionLookup.set(section.DocumentSectionId, section);

      if (section.ParentDocumentSectionId) {
        const siblings = this.childLookup.get(section.ParentDocumentSectionId) || [];
        siblings.push(section);
        this.childLookup.set(section.ParentDocumentSectionId, siblings);
      }
    });
    this.syncFavoriteStateForSections(sortedSections, this.activeReferenceDocumentItemId ?? this.documentItem?.DocumentItemId);

    const hasFarHierarchy = sortedSections.some(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Subchapter)
      && sortedSections.some(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Part);

    const hasAimHierarchy = this.isAimPublication(sortedSections);

    if (hasFarHierarchy) {
      this.viewerMode = 'reference';
      this.referenceKind = 'far';
      this.activeReferenceTab = 'far';
      this.buildFarViewModel(sortedSections);
      return;
    }

    if (hasAimHierarchy) {
      this.viewerMode = 'reference';
      this.referenceKind = 'aim';
      this.activeReferenceTab = 'aim';
      this.buildAimViewModel(sortedSections);
      return;
    }

    this.viewerMode = 'generic';
    this.referenceKind = 'far';
    this.activeReferenceTab = 'far';
    this.buildGenericViewModel(sortedSections);
  }

  private isAimPublication(sortedSections: StructuredDocumentSectionModel[]): boolean {
    if (this.viewerData?.DocumentPublicationTypeId === DocumentPublicationTypeAllItems.AIM) {
      return true;
    }

    return sortedSections.some(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Chapter)
      && sortedSections.some(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Section);
  }

  private buildGenericViewModel(sortedSections: StructuredDocumentSectionModel[]): void {
    const navLookup = new Map<number, StructuredDocumentNavItemModel>();
    const rootItems: StructuredDocumentNavItemModel[] = [];

    sortedSections.forEach(section => {
      if (section.DocumentSectionId) {
        navLookup.set(section.DocumentSectionId, { section, children: [] });
      }
    });

    sortedSections.forEach(section => {
      const navItem = section.DocumentSectionId ? navLookup.get(section.DocumentSectionId) : null;

      if (!navItem) {
        return;
      }

      if (section.ParentDocumentSectionId && navLookup.has(section.ParentDocumentSectionId)) {
        navLookup.get(section.ParentDocumentSectionId)?.children.push(navItem);
      } else {
        rootItems.push(navItem);
      }
    });

    this.navTree = rootItems;
    this.leafSections = sortedSections.filter(section => this.isGenericLeafSection(section));
    this.activeSection = this.leafSections[0] || sortedSections[0] || null;
    this.activeIndex = this.activeSection
      ? Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === this.activeSection?.DocumentSectionId))
      : 0;

    if (this.activeSection) {
      this.activeSection = this.resolveGenericTargetSection(this.activeSection);
      this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === this.activeSection?.DocumentSectionId));
      this.syncPageOptions();
      this.syncReferencedSections();
      this.navigateToFirstSectionReference();
      this.ensureSectionContentLoaded(this.activeSection);
      return;
    }

    this.syncPageOptions();
    this.syncReferencedSections();
  }

  private buildFarViewModel(sortedSections: StructuredDocumentSectionModel[]): void {
    const subchapters = sortedSections.filter(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Subchapter);

    this.tocGroups = subchapters
      .map(subchapter => this.buildTocGroup(
        subchapter,
        (this.childLookup.get(subchapter.DocumentSectionId || 0) || [])
          .filter(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Part)
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
      ))
      .filter(group => group.parts.length > 0);

    this.displayTocGroups = this.tocGroups;
    this.syncReferencedSections();
    this.navigateToFirstSectionReference();
  }

  private buildAimViewModel(sortedSections: StructuredDocumentSectionModel[]): void {
    const chapters = sortedSections.filter(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Chapter);
    const appendixSections = sortedSections
      .filter(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Appendix && !section.ParentDocumentSectionId)
      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

    this.tocGroups = chapters
      .map(chapter => this.buildTocGroup(
        chapter,
        (this.childLookup.get(chapter.DocumentSectionId || 0) || [])
          .filter(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Section)
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
      ))
      .filter(group => group.parts.length > 0);

    if (appendixSections.length) {
      this.tocGroups.push(this.buildTocGroup(
        {
          Title: 'Appendices',
          SectionKey: 'appendices',
          Content: [],
          Anchors: [],
          Assets: []
        },
        appendixSections
      ));
    }

    this.displayTocGroups = this.tocGroups;
    this.syncReferencedSections();
    this.navigateToFirstSectionReference();
  }

  private buildTocGroup(
    heading: StructuredDocumentSectionModel,
    parts: StructuredDocumentSectionModel[]
  ): StructuredDocumentTocGroupModel {
    return {
      key: heading.DocumentSectionId || heading.SectionKey || heading.Title || '',
      eyebrow: heading.Label || '',
      title: heading.Title || heading.Label || '',
      parts: parts.map(part => {
        const meta = this.buildTocPartMeta(part);
        if (part.DocumentSectionId) {
          this.tocMetaByPartId.set(part.DocumentSectionId, meta);
        }

        return { part, rangeText: meta.rangeText, countText: meta.countText, children: [] };
      })
    };
  }

  private buildTocPartMeta(part: StructuredDocumentSectionModel): StructuredDocumentTocPartMetaModel {
    const leaves = this.collectLeafSections(part);
    if (!leaves.length || (leaves.length === 1 && leaves[0].DocumentSectionId === part.DocumentSectionId)) {
      return { rangeText: '', countText: '' };
    }

    const firstLabel = this.tocLeafLabel(leaves[0]);
    const lastLabel = this.tocLeafLabel(leaves[leaves.length - 1]);
    const rangeText = firstLabel && lastLabel
      ? (leaves.length > 1 && firstLabel !== lastLabel ? `${firstLabel} – ${lastLabel}` : firstLabel)
      : '';

    const appendixCount = leaves.filter(leaf => leaf.DocumentSectionTypeId === DocumentSectionTypeAllItems.Appendix).length;
    const leafCount = leaves.length - appendixCount;
    const leafNoun = this.referenceKind === 'aim' ? 'paragraph' : 'section';
    const countParts: string[] = [];

    if (leafCount) {
      countParts.push(`${leafCount} ${leafCount === 1 ? leafNoun : `${leafNoun}s`}`);
    }

    if (appendixCount) {
      countParts.push(`${appendixCount} ${appendixCount === 1 ? 'appendix' : 'appendices'}`);
    }

    return { rangeText, countText: countParts.join(' · ') };
  }

  private tocLeafLabel(section: StructuredDocumentSectionModel): string {
    return (section?.Label || section?.SectionKey || '').replace(/^§\s*/, '').trim();
  }

  private applyActivePartLeaves(): void {
    this.leafSections = this.activePart ? this.collectLeafSections(this.activePart) : [];

    if (!this.leafSections.length) {
      this.activeSection = this.activePart;
      this.activeIndex = 0;
      this.syncPageOptions();
      return;
    }

    if (!this.activeSection || !this.leafSections.some(section => section.DocumentSectionId === this.activeSection?.DocumentSectionId)) {
      this.activeSection = this.leafSections[0];
    }

    this.activeIndex = Math.max(0, this.leafSections.findIndex(item => item.DocumentSectionId === this.activeSection?.DocumentSectionId));
    this.syncPageOptions();
    if (this.activeSection) {
      this.ensureSectionContentLoaded(this.activeSection);
    }
  }

  private syncPageOptions(): void {
    this.selectedLeafSectionId = this.activeSection?.DocumentSectionId || null;
    this.pageOptions = this.leafSections
      .filter(section => !!section.DocumentSectionId)
      .map(section => ({
        documentSectionId: section.DocumentSectionId || 0,
        label: this.buildPageOptionLabel(section)
      }));
  }

  private syncReferencedSections(): void {
    if (!this.sectionReferences?.length || !this.sectionLookup.size) {
      this.referencedSections = [];
      this.syncReferenceDisplaySections();
      return;
    }

    const activeDocumentItemId = this.activeReferenceDocumentItemId ?? this.documentItem?.DocumentItemId ?? this.viewerData?.DocumentItemId;
    const seenSectionIds = new Set<number>();
    const referencedSections: StructuredDocumentReferenceDisplayModel[] = [];

    this.sectionReferences
      .filter(reference => !reference.documentItemId || !activeDocumentItemId || reference.documentItemId === activeDocumentItemId)
      .forEach(reference => {
        const resolvedReference = this.resolveSectionReference(reference);
        if (!resolvedReference || seenSectionIds.has(resolvedReference.documentSectionId)) {
          return;
        }

        seenSectionIds.add(resolvedReference.documentSectionId);
        referencedSections.push({
          ...resolvedReference,
          displayTitle: this.buildSectionReferenceDisplayTitle(resolvedReference)
        });
      });

    this.referencedSections = referencedSections;
    this.syncReferenceDisplaySections();
  }

  private resolveSectionReference(reference: StructuredDocumentSectionReferenceModel): StructuredDocumentResolvedReferenceModel | null {
    const referencedSection = this.sectionLookup.get(reference.documentSectionId);
    const targetSection = referencedSection ? this.resolveReferenceTargetSection(referencedSection) : null;

    if (!targetSection?.DocumentSectionId) {
      return null;
    }

    return {
      ...reference,
      documentSectionId: targetSection.DocumentSectionId,
      label: reference.label || targetSection.Label || targetSection.SectionKey || '',
      title: reference.title || targetSection.Title,
      section: targetSection
    };
  }

  private syncReferenceDisplaySections(): void {
    if (!this.referencedSections.length) {
      this.primaryReferencedSection = null;
      this.overflowReferencedSections = [];
      this.closeReferenceOverflow();
      return;
    }

    const activeSectionId = this.activeSection?.DocumentSectionId;
    const primaryReference = this.referencedSections.find(reference => reference.section?.DocumentSectionId === activeSectionId)
      || this.referencedSections[0];

    this.primaryReferencedSection = primaryReference;
    this.overflowReferencedSections = this.referencedSections.filter(reference => reference !== primaryReference);

    if (!this.overflowReferencedSections.length) {
      this.closeReferenceOverflow();
    }
  }

  private buildSectionReferenceDisplayTitle(reference: StructuredDocumentResolvedReferenceModel): string {
    const locator = reference.section?.Label
      || reference.label
      || reference.section?.SectionKey
      || '';
    const title = reference.title || reference.section?.Title || '';

    if (locator && title) {
      return title.toLowerCase().includes(locator.toLowerCase())
        ? title
        : `${locator} ${title}`;
    }

    return locator || title || 'Referenced section';
  }

  private navigateToFirstSectionReference(): void {
    if (!this.referencedSections.length) {
      return;
    }

    this.navigateToReferencedSection(this.referencedSections[0].section);
  }

  private navigateToReferencedSection(section: StructuredDocumentSectionModel): void {
    if (!section?.DocumentSectionId) {
      return;
    }

    if (this.isReferenceLayout) {
      const referencePart = this.findReferencePart(section);
      if (referencePart) {
        this.activePart = referencePart;
        this.applyActivePartLeaves();
      }

      // The TOC-first layout only renders sections in reading view, so a
      // lesson reference must leave the contents page before selecting.
      this.referenceView = 'reading';
      this.isPageMenuOpen = false;
    }

    this.selectLeafSection(section);
  }

  private resolveReferenceTargetSection(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel | null {
    if (this.isReferenceLayout) {
      const leafSections = this.collectLeafSections(section);
      return leafSections.find(item => item.DocumentSectionId === section.DocumentSectionId) || leafSections[0] || section;
    }

    return this.resolveGenericTargetSection(section);
  }

  private findReferencePart(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel | null {
    if (this.referenceKind === 'aim') {
      if (section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Section
        || section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Appendix) {
        return section;
      }

      return this.findAncestorSection(section, [DocumentSectionTypeAllItems.Section, DocumentSectionTypeAllItems.Appendix]);
    }

    if (section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Part) {
      return section;
    }

    return this.findAncestorSection(section, [DocumentSectionTypeAllItems.Part]);
  }

  private findAncestorSection(section: StructuredDocumentSectionModel, documentSectionTypeIds: number[]): StructuredDocumentSectionModel | null {
    let current = section;

    while (current?.ParentDocumentSectionId) {
      const parent = this.sectionLookup.get(current.ParentDocumentSectionId);
      if (!parent) {
        return null;
      }

      if (documentSectionTypeIds.includes(parent.DocumentSectionTypeId || 0)) {
        return parent;
      }

      current = parent;
    }

    return null;
  }

  private buildPageOptionLabel(section: StructuredDocumentSectionModel): string {
    const label = section?.Label || section?.SectionKey || '';
    return label || section?.Title || '';
  }

  private scrollActivePageMenuItemIntoView(): void {
    setTimeout(() => {
      const activeMenuItem = this.hostElementRef.nativeElement.querySelector<HTMLElement>('.sdv-page-menu__item.is-active');
      activeMenuItem?.scrollIntoView({ block: 'center', inline: 'nearest' });
    });
  }

  private collectLeafSections(part: StructuredDocumentSectionModel): StructuredDocumentSectionModel[] {
    if (this.referenceKind === 'aim') {
      return this.collectAimLeafSections(part);
    }

    const sections: StructuredDocumentSectionModel[] = [];
    const walk = (current: StructuredDocumentSectionModel) => {
      const currentId = current.DocumentSectionId;
      const children = currentId ? (this.childLookup.get(currentId) || []) : [];

      if (!children.length && (current.DocumentSectionTypeId === DocumentSectionTypeAllItems.Section
        || current.DocumentSectionTypeId === DocumentSectionTypeAllItems.Appendix)) {
        sections.push(current);
        return;
      }

      children
        .slice()
        .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
        .forEach(child => walk(child));
    };

    walk(part);
    return sections;
  }

  private collectAimLeafSections(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel[] {
    const sectionId = section.DocumentSectionId;
    const children = sectionId ? (this.childLookup.get(sectionId) || []) : [];
    const paragraphs = children
      .filter(child => child.DocumentSectionTypeId === DocumentSectionTypeAllItems.Paragraph)
      .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

    if (paragraphs.length) {
      return paragraphs;
    }

    return [section];
  }

  private parseSectionLabel(label: string): { regNumber: string; eyebrow: string } {
    const trimmed = (label || '').trim();
    if (!trimmed) {
      return { regNumber: '', eyebrow: '' };
    }

    if (!/[a-zA-Z]/.test(trimmed)) {
      return { regNumber: trimmed, eyebrow: '' };
    }

    const match = trimmed.match(/^(.+?\s)(\d[\d.\-]*)\s*$/);
    if (match) {
      return {
        eyebrow: match[1].trim(),
        regNumber: match[2]
      };
    }

    return { regNumber: trimmed, eyebrow: '' };
  }

  private isGenericLeafSection(section: StructuredDocumentSectionModel): boolean {
    const sectionId = section.DocumentSectionId;
    const children = sectionId ? (this.childLookup.get(sectionId) || []) : [];

    if (children.length) {
      return false;
    }

    return section.DocumentSectionTypeId === 6
      || section.DocumentSectionTypeId === 7
      || section.DocumentSectionTypeId === 8
      || !section.DocumentSectionTypeId;
  }

  private resolveGenericTargetSection(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel {
    const descendants = this.collectGenericLeafSections(section);
    return descendants[0] || section;
  }

  private collectGenericLeafSections(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel[] {
    const sections: StructuredDocumentSectionModel[] = [];
    const walk = (current: StructuredDocumentSectionModel) => {
      const currentId = current.DocumentSectionId;
      const children = currentId ? (this.childLookup.get(currentId) || []) : [];

      if (!children.length) {
        if (this.isGenericLeafSection(current)) {
          sections.push(current);
        }

        return;
      }

      children
        .slice()
        .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
        .forEach(child => walk(child));
    };

    walk(section);
    return sections;
  }

  private seedPrimaryPublicationCache(): void {
    if (!this.viewerData || !this.isReferenceLayout) {
      return;
    }

    this.viewerDataCache.set(this.referenceKind, this.viewerData);

    if (this.documentItem?.DocumentItemId) {
      this.documentItemIdByKind.set(this.referenceKind, this.documentItem.DocumentItemId);
      this.activeReferenceDocumentItemId = this.documentItem.DocumentItemId;
    }

    this.documentItemIdByKind.set('aim', this.aimReferenceDocumentItemId);

    if (this.referenceKind === 'far' && this.documentItem?.DocumentItemId) {
      this.documentItemIdByKind.set('far', this.documentItem.DocumentItemId);
    }
  }

  private applyPublication(kind: ReferencePublicationKind, viewer: StructuredDocumentViewerModel): void {
    this.viewerData = viewer;
    this.activeReferenceDocumentItemId = this.documentItemIdByKind.get(kind);
    this.activeReferenceTab = kind;
    this.loadedSectionIds.clear();
    this.loadingSectionIds.clear();
    this.isSectionLoading = false;
    this.sectionLoadError = '';
    this.referenceSwitchError = '';
    this.isPageMenuOpen = false;
    this.resetHierarchySearchState();
    this.rebuildViewModel();
    this.rebuildFavoriteGroups();
    this.applyRouteState();

    if (this.pendingFavorite) {
      const favorite = this.pendingFavorite;
      this.pendingFavorite = null;
      this.openResolvedFavorite(favorite);
    }
  }

  private showPartContents(part: StructuredDocumentSectionModel): void {
    this.contentsPart = part;
    this.contentsSections = this.buildPartContentsSections(part);
    this.contentsPartEyebrow = this.buildPartContentsEyebrow(part);
    this.contentsPartMeta = this.tocMetaByPartId.get(part.DocumentSectionId || 0) || null;
    this.isPageMenuOpen = false;
    this.clearReferenceSearch();
    this.referenceView = 'part';
  }

  private buildPartContentsSections(part: StructuredDocumentSectionModel): StructuredDocumentSectionModel[] {
    // Flat list, no subpart grouping; appendices sink to the end.
    const leaves = this.collectLeafSections(part);
    return [
      ...leaves.filter(section => section.DocumentSectionTypeId !== DocumentSectionTypeAllItems.Appendix),
      ...leaves.filter(section => section.DocumentSectionTypeId === DocumentSectionTypeAllItems.Appendix)
    ];
  }

  private buildPartContentsEyebrow(part: StructuredDocumentSectionModel): string {
    const parent = part.ParentDocumentSectionId ? this.sectionLookup.get(part.ParentDocumentSectionId) : null;
    if (!parent) {
      return this.referenceMetaText;
    }

    return parent.Label && parent.Title
      ? `${parent.Label} — ${parent.Title}`
      : (parent.Title || parent.Label || '');
  }

  private navigateToRoute(pub: ReferencePublicationKind, partId: number | null): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { pub, part: partId, section: null },
      queryParamsHandling: 'merge'
    });
  }

  private applyRouteState(): void {
    if (!this.enableRouteSync || !this.isReferenceLayout || !this.viewerData) {
      return;
    }

    if (this.routePub && this.routePub !== this.activeReferenceTab) {
      this.isApplyingRoutePub = true;
      try {
        this.selectReferenceTab(this.routePub);
      } finally {
        this.isApplyingRoutePub = false;
      }
      // The part param is applied after the publication loads (applyPublication
      // re-enters this method).
      return;
    }

    if (this.routePartId) {
      if (this.contentsPart?.DocumentSectionId === this.routePartId && this.referenceView !== 'toc') {
        return;
      }

      const part = this.sectionLookup.get(this.routePartId);
      if (part) {
        this.showPartContents(part);
      }
      return;
    }

    if (this.routeSectionReferenceKey) {
      const section = Array.from(this.sectionLookup.values()).find(item =>
        (item.SectionKey || '').trim().toLowerCase() === this.routeSectionReferenceKey);
      if (section) {
        this.openResolvedFavorite({
          DocumentItemId: this.activeReferenceDocumentItemId,
          SectionReferenceKey: this.routeSectionReferenceKey,
          CurrentDocumentSectionId: section.DocumentSectionId,
          IsAvailable: true
        });
      }
      return;
    }

    if (this.referenceView !== 'toc') {
      this.isPageMenuOpen = false;
      this.referenceView = 'toc';
    }
  }

  private searchReferenceHierarchy(searchText: string, searchVersion: number) {
    const trimmedSearchText = (searchText || '').trim();
    if (!trimmedSearchText || !this.isReferenceLayout) {
      this.resetHierarchySearchState();
      return of(null);
    }

    const documentItemId = this.activeReferenceDocumentItemId ?? this.documentItem?.DocumentItemId;
    const documentVersionId = this.viewerData?.DocumentVersionId;
    if (!documentItemId) {
      return of(null);
    }

    if (!this.isCurrentHierarchySearch(searchVersion, trimmedSearchText)) {
      return of(null);
    }

    this.isHierarchySearchLoading = true;
    this.isHierarchySearchPending = false;
    this.hierarchySearchError = '';

    return this.structuredDocumentViewerService.getStructuredDocumentHierarchy(documentItemId, documentVersionId, trimmedSearchText)
      .pipe(
        catchError(() => {
          if (this.isCurrentHierarchySearch(searchVersion, trimmedSearchText)) {
            this.searchTocGroups = [];
            this.displayTocGroups = [];
            this.hierarchySearchError = 'Unable to search sections right now.';
          }
          return of(null);
        }),
        finalize(() => {
          if (this.isCurrentHierarchySearch(searchVersion, trimmedSearchText)) {
            this.isHierarchySearchLoading = false;
            this.isHierarchySearchPending = false;
          }
        }),
        switchMap(hierarchy => {
          if (this.hierarchySearchError || !this.isCurrentHierarchySearch(searchVersion, trimmedSearchText)) {
            return of(hierarchy);
          }

          if (!hierarchy?.Groups?.length) {
            this.searchTocGroups = [];
            this.displayTocGroups = [];
            return of(hierarchy);
          }

          this.applyHierarchySearchResults(hierarchy?.Groups || []);
          return of(hierarchy);
        })
      );
  }

  private applyHierarchySearchResults(groups: StructuredDocumentHierarchyGroupModel[]): void {
    this.searchTocGroups = groups
      .map(group => this.mapHierarchySearchGroup(group))
      .filter(group => group.parts.length > 0);
    this.displayTocGroups = this.searchTocGroups;
  }

  private mapHierarchySearchGroup(group: StructuredDocumentHierarchyGroupModel): StructuredDocumentTocGroupModel {
    const heading = this.mapHierarchySection(group?.Section);

    return {
      key: heading.DocumentSectionId || heading.SectionKey || heading.Title || '',
      eyebrow: heading.Label || '',
      title: heading.Title || heading.Label || '',
      parts: (group?.Parts || [])
        .map(part => this.mapHierarchySearchPart(part))
        .filter(part => !!part.part.DocumentSectionId)
    };
  }

  private mapHierarchySearchPart(part: StructuredDocumentHierarchyPartModel): StructuredDocumentTocPartModel {
    const mappedPart = this.mapHierarchySection(part?.Section);
    const meta = this.tocMetaByPartId.get(mappedPart?.DocumentSectionId || 0);
    const children = (part?.Children || [])
      .map(child => this.mapHierarchySection(child))
      .filter(child => !!child?.DocumentSectionId);

    return {
      part: mappedPart,
      rangeText: meta?.rangeText || '',
      countText: meta?.countText || '',
      children
    };
  }

  private mapHierarchySection(section: StructuredDocumentHierarchySectionModel | undefined): StructuredDocumentSectionModel {
    if (section?.DocumentSectionId) {
      const existingSection = this.sectionLookup.get(section.DocumentSectionId);
      if (existingSection) {
        return existingSection;
      }
    }

    return {
      DocumentSectionId: section?.DocumentSectionId,
      ParentDocumentSectionId: section?.ParentDocumentSectionId,
      DocumentSectionTypeId: section?.DocumentSectionTypeId,
      DocumentSectionType: section?.DocumentSectionType,
      SectionKey: section?.SectionKey,
      SectionSlug: section?.SectionSlug,
      Label: section?.Label,
      Title: section?.Title,
      DisplayOrder: section?.DisplayOrder,
      Content: [],
      Anchors: [],
      Assets: []
    };
  }

  private isCurrentHierarchySearch(searchVersion: number, searchText: string): boolean {
    return searchVersion === this.hierarchySearchVersion
      && (this.searchText || '').trim() === searchText
      && this.isReferenceLayout;
  }

  private resetHierarchySearchState(): void {
    this.searchTocGroups = [];
    this.displayTocGroups = this.tocGroups;
    this.hierarchySearchVersion++;
    this.isHierarchySearchLoading = false;
    this.isHierarchySearchPending = false;
    this.hierarchySearchError = '';
  }

  private ensureSectionContentLoaded(section: StructuredDocumentSectionModel | null): void {
    const documentItemId = this.activeReferenceDocumentItemId ?? this.documentItem?.DocumentItemId;
    const documentSectionId = section?.DocumentSectionId;
    const documentVersionId = this.viewerData?.DocumentVersionId;

    if (!documentItemId || !documentSectionId) {
      return;
    }

    if (this.loadedSectionIds.has(documentSectionId) || (section.Content && section.Content.length)) {
      this.loadedSectionIds.add(documentSectionId);
      return;
    }

    if (this.loadingSectionIds.has(documentSectionId)) {
      return;
    }

    this.loadingSectionIds.add(documentSectionId);
    this.isSectionLoading = true;
    this.sectionLoadError = '';

    this.structuredDocumentViewerService.getStructuredDocumentSection(documentItemId, documentSectionId, documentVersionId)
      .subscribe({
        next: loadedSection => {
          this.loadingSectionIds.delete(documentSectionId);
          this.loadedSectionIds.add(documentSectionId);
          this.mergeLoadedSection(loadedSection);
          this.isSectionLoading = false;
        },
        error: error => {
          this.loadingSectionIds.delete(documentSectionId);
          this.isSectionLoading = false;
          this.sectionLoadError = error?.message || 'Unable to load this section right now.';
        }
      });
  }

  private mergeLoadedSection(loadedSection: StructuredDocumentSectionModel): void {
    const documentSectionId = loadedSection?.DocumentSectionId;
    if (!documentSectionId) {
      return;
    }

    const targetSection = this.sectionLookup.get(documentSectionId);
    if (!targetSection) {
      return;
    }

    targetSection.Content = loadedSection.Content || [];
    targetSection.Anchors = loadedSection.Anchors || [];
    targetSection.Assets = loadedSection.Assets || [];
    targetSection.IsFavorite = loadedSection.IsFavorite ?? targetSection.IsFavorite;
  }

  private loadFavorites(force: boolean = false): void {
    if (!this.isReferenceLayout || this.favoritesLoading || (this.favoritesLoaded && !force)) {
      this.rebuildFavoriteGroups();
      return;
    }

    this.favoritesLoading = true;
    this.favoritesError = '';

    this.structuredDocumentViewerService.getStructuredDocumentFavorites()
      .pipe(finalize(() => {
        this.favoritesLoading = false;
      }))
      .subscribe({
        next: favorites => {
          this.favorites = favorites || [];
          this.favoritesLoaded = true;
          this.syncFavoriteStateAcrossCache();
          this.rebuildFavoriteGroups();
        },
        error: () => {
          this.favoritesError = 'Unable to load your favorites right now.';
        }
      });
  }

  private rebuildFavoriteGroups(): void {
    const search = (this.searchText || '').trim().toLowerCase();
    const selectedPublicationTypeId = this.activeReferenceTab === 'aim'
      ? DocumentPublicationTypeAllItems.AIM
      : DocumentPublicationTypeAllItems.FAR;
    const filteredFavorites = this.favorites.filter(favorite => {
      if (favorite.DocumentPublicationTypeId !== selectedPublicationTypeId) {
        return false;
      }

      if (!search) {
        return true;
      }

      return [
        favorite.ReferenceLocatorText,
        favorite.Label,
        favorite.Title,
        favorite.SectionReferenceKey,
        favorite.PublicationName
      ].some(value => (value || '').toLowerCase().includes(search));
    });

    const available = filteredFavorites.filter(favorite => favorite.IsAvailable);
    const unavailable = filteredFavorites.filter(favorite => !favorite.IsAvailable);
    const groups: StructuredDocumentFavoriteGroupModel[] = [];

    if (available.length) {
      groups.push({
        key: this.activeReferenceTab,
        title: this.activeReferenceTab === 'aim'
          ? 'Aeronautical Information Manual'
          : 'Federal Aviation Regulations',
        items: available
      });
    }
    if (unavailable.length) {
      groups.push({ key: 'unavailable', title: 'Unavailable references', items: unavailable });
    }

    this.displayFavoriteGroups = groups;
  }

  private syncFavoriteStateAcrossCache(): void {
    this.viewerDataCache.forEach((viewer, kind) => {
      this.syncFavoriteStateForSections(viewer.Sections || [], this.documentItemIdByKind.get(kind));
    });
    this.syncFavoriteStateForSections(
      this.viewerData?.Sections || [],
      this.activeReferenceDocumentItemId ?? this.documentItem?.DocumentItemId
    );
  }

  private syncFavoriteStateForSections(
    sections: StructuredDocumentSectionModel[],
    documentItemId?: number
  ): void {
    if (!documentItemId) {
      return;
    }

    const keys = new Set(
      this.favorites
        .filter(favorite => favorite.DocumentItemId === documentItemId)
        .map(favorite => (favorite.SectionReferenceKey || '').trim().toLowerCase())
    );

    sections.forEach(section => {
      section.IsFavorite = keys.has((section.SectionKey || '').trim().toLowerCase());
    });
  }

  private syncSectionFavoriteAcrossCache(
    documentItemId: number,
    sectionReferenceKey: string,
    isFavorite: boolean,
    isSaving: boolean
  ): void {
    const normalizedKey = (sectionReferenceKey || '').trim().toLowerCase();
    const sync = (sections: StructuredDocumentSectionModel[], itemId?: number) => {
      if (itemId !== documentItemId) {
        return;
      }

      sections
        .filter(section => (section.SectionKey || '').trim().toLowerCase() === normalizedKey)
        .forEach(section => {
          section.IsFavorite = isFavorite;
          section.FavoriteSaving = isSaving;
        });
    };

    this.viewerDataCache.forEach((viewer, kind) =>
      sync(viewer.Sections || [], this.documentItemIdByKind.get(kind)));
    sync(
      this.viewerData?.Sections || [],
      this.activeReferenceDocumentItemId ?? this.documentItem?.DocumentItemId
    );
  }

  private openResolvedFavorite(favorite: StructuredDocumentFavoriteModel): void {
    const sectionById = favorite.CurrentDocumentSectionId
      ? this.sectionLookup.get(favorite.CurrentDocumentSectionId)
      : null;
    const normalizedKey = (favorite.SectionReferenceKey || '').trim().toLowerCase();
    const section = sectionById || Array.from(this.sectionLookup.values()).find(item =>
      (item.SectionKey || '').trim().toLowerCase() === normalizedKey);
    if (!section) {
      this.favoriteActionError = 'This favorite is not available in the current publication.';
      return;
    }

    const part = this.findReferencePart(section);
    if (part) {
      this.activePart = part;
      this.applyActivePartLeaves();
    }

    this.selectLeafSection(section);
    this.referenceView = 'reading';
  }
}
